// Privacy policy for the real PSS_MAN app (self-hosted server + Chrome
// extension + optional Google Drive / OneDrive backup), served at
// /pss_man/privacy. Kept out of siteConfig on purpose: legal text must not be
// editable from the page builder or machine-translated into every locale.
// English is authoritative; Spanish is provided for convenience.
//
// Every claim here was checked against the pps_man source on 2026-09-27.
// Re-check it when the app's data handling changes (new endpoints, new
// cloud providers, new extension permissions).

const CONTACT_EMAIL = 'fernando-gonzalezleon92@outlook.com';

const en = {
  title: 'PSS_MAN Privacy Policy',
  effective: 'Effective date: September 27, 2026',
  intro: [
    'PSS_MAN is a self-hosted password manager made by Fernando González León. It consists of a web app that you run on your own hardware (for example, a Raspberry Pi on your home network) and an optional Chrome extension. This policy explains what data PSS_MAN handles, where that data is kept, and who can see it.',
  ],
  sections: [
    {
      heading: 'The short version',
      bullets: [
        'PSS_MAN runs on a server you control. The developer does not operate any PSS_MAN server and never receives, collects or has access to your vault, your credentials or your usage data.',
        'There are no ads, analytics, tracking or telemetry.',
        'Nothing is sold or shared. Data only leaves your server if you turn on cloud backup, and then it goes to your own Google Drive or OneDrive account.',
        'If someone else runs PSS_MAN for you, that person (the server operator) controls the server and its data.',
      ],
    },
    {
      heading: 'Data stored on your server',
      paragraphs: [
        'PSS_MAN stores its data as files on the server where it is installed:',
      ],
      bullets: [
        'Vault entries (logins, API keys and secure notes) and their folders. Vault contents are encrypted with AES-256-GCM using a key protected by your master password (derived with Argon2id). Your master password itself is never stored.',
        'Account information: username, role, whether two-factor authentication (TOTP) is enabled, and password-change and backup dates.',
        'Trusted devices, if you choose to trust a browser: a device name, a browser description, when it was added and last used, and the IP address it was used from.',
        'Cloud-backup connection details, if cloud backup is enabled (see below).',
      ],
    },
    {
      heading: 'Data stored in your browser',
      bullets: [
        'A session cookie that keeps you signed in, and a trusted-device cookie if you choose to trust that browser.',
        'Display preferences in local storage: theme, language, the last username used on that browser, and how the vault list is shown.',
      ],
    },
    {
      heading: 'The Chrome extension',
      paragraphs: [
        'The extension fills saved logins into websites. To do that it requests access to the pages you visit and the "activeTab", "tabs" and "storage" permissions.',
      ],
      bullets: [
        'It stores one setting: the address of your PSS_MAN server.',
        'When you open the extension, it sends the address of the current page to your own PSS_MAN server so the server can find matching logins.',
        'When you choose a login, it fills the username and password fields on that page. It does not read, collect or send any other page content.',
        'It sends nothing to the developer or to any other third party. Uninstalling the extension removes its stored setting.',
      ],
    },
    {
      heading: 'Optional cloud backup (Google Drive and Microsoft OneDrive)',
      paragraphs: [
        'An administrator of your PSS_MAN server can choose to connect a Google Drive or Microsoft OneDrive account for automatic backups. Nothing is uploaded unless this is set up.',
      ],
      bullets: [
        'Backups are uploaded directly from your server to your own cloud account. They never pass through the developer or any other service.',
        'Google Drive access uses the "drive.file" permission, so PSS_MAN can only see and manage files it created itself. It cannot see any of your other files.',
        'OneDrive access uses the "Files.ReadWrite.AppFolder" permission, which limits PSS_MAN to its own app folder.',
        'Backup archives contain your vault files, which are already encrypted. They can optionally be sealed a second time with a backup passphrase.',
        'The access tokens that let your server upload are stored on your server, encrypted.',
        'You can disconnect at any time from PSS_MAN, and revoke access from your Google or Microsoft account settings.',
      ],
    },
    {
      heading: 'Google API data',
      paragraphs: [
        "PSS_MAN's use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements. Data obtained through Google APIs is used only to store and manage your PSS_MAN backup files in your own Google Drive. It is not used for advertising, is not sold, is not transferred to anyone else, and is not read by humans.",
      ],
    },
    {
      heading: 'Other third-party services',
      bullets: [
        'The web interface loads its typeface (Inter) from Google Fonts, so your browser connects to Google\'s font servers when you open PSS_MAN. Google\'s own privacy policy applies to that connection.',
        'Google and Microsoft are only contacted by your server if you connect cloud backup.',
      ],
    },
    {
      heading: 'Keeping and deleting data',
      paragraphs: [
        'Because the data lives on your server and in your own accounts, you decide how long it is kept. You can delete entries and users inside PSS_MAN, delete the server\'s data directory, delete backup files from your Google Drive or OneDrive, and remove the extension from Chrome.',
      ],
    },
    {
      heading: 'Children',
      paragraphs: [
        'PSS_MAN is not directed at children under 13, and the developer does not knowingly collect information from children.',
      ],
    },
    {
      heading: 'Changes to this policy',
      paragraphs: [
        'If PSS_MAN starts handling data differently, this page will be updated and the effective date above will change.',
      ],
    },
    {
      heading: 'Contact',
      paragraphs: ['Questions about this policy can be sent to:'],
      email: CONTACT_EMAIL,
    },
  ],
};

const es = {
  title: 'Política de privacidad de PSS_MAN',
  effective: 'Fecha de entrada en vigor: 27 de septiembre de 2026',
  note: 'Esta traducción se ofrece por comodidad. En caso de diferencia, prevalece la versión en inglés.',
  intro: [
    'PSS_MAN es un gestor de contraseñas autoalojado creado por Fernando González León. Está formado por una aplicación web que ejecutas en tu propio equipo (por ejemplo, una Raspberry Pi en tu red doméstica) y una extensión opcional para Chrome. Esta política explica qué datos maneja PSS_MAN, dónde se guardan y quién puede verlos.',
  ],
  sections: [
    {
      heading: 'En resumen',
      bullets: [
        'PSS_MAN se ejecuta en un servidor que controlas tú. El desarrollador no opera ningún servidor de PSS_MAN y nunca recibe, recopila ni tiene acceso a tu bóveda, tus credenciales ni tus datos de uso.',
        'No hay anuncios, analítica, rastreo ni telemetría.',
        'No se vende ni se comparte nada. Los datos solo salen de tu servidor si activas la copia de seguridad en la nube, y en ese caso van a tu propia cuenta de Google Drive u OneDrive.',
        'Si otra persona ejecuta PSS_MAN por ti, esa persona (quien opera el servidor) controla el servidor y sus datos.',
      ],
    },
    {
      heading: 'Datos guardados en tu servidor',
      paragraphs: [
        'PSS_MAN guarda sus datos como archivos en el servidor donde está instalado:',
      ],
      bullets: [
        'Las entradas de la bóveda (accesos, claves de API y notas seguras) y sus carpetas. El contenido de la bóveda se cifra con AES-256-GCM usando una clave protegida por tu contraseña maestra (derivada con Argon2id). La contraseña maestra en sí nunca se guarda.',
        'Información de la cuenta: nombre de usuario, rol, si la verificación en dos pasos (TOTP) está activada, y las fechas de cambio de contraseña y de copia de seguridad.',
        'Dispositivos de confianza, si decides confiar en un navegador: un nombre de dispositivo, una descripción del navegador, cuándo se añadió y se usó por última vez, y la dirección IP desde la que se usó.',
        'Los datos de conexión de la copia en la nube, si está activada (ver más abajo).',
      ],
    },
    {
      heading: 'Datos guardados en tu navegador',
      bullets: [
        'Una cookie de sesión que mantiene tu sesión iniciada, y una cookie de dispositivo de confianza si decides confiar en ese navegador.',
        'Preferencias de visualización en el almacenamiento local: tema, idioma, el último nombre de usuario usado en ese navegador y cómo se muestra la lista de la bóveda.',
      ],
    },
    {
      heading: 'La extensión de Chrome',
      paragraphs: [
        'La extensión rellena accesos guardados en los sitios web. Para ello solicita acceso a las páginas que visitas y los permisos "activeTab", "tabs" y "storage".',
      ],
      bullets: [
        'Guarda un único ajuste: la dirección de tu servidor PSS_MAN.',
        'Cuando abres la extensión, envía la dirección de la página actual a tu propio servidor PSS_MAN para que busque los accesos que coinciden.',
        'Cuando eliges un acceso, rellena los campos de usuario y contraseña de esa página. No lee, recopila ni envía ningún otro contenido de la página.',
        'No envía nada al desarrollador ni a ningún otro tercero. Al desinstalar la extensión se elimina el ajuste guardado.',
      ],
    },
    {
      heading: 'Copia de seguridad opcional en la nube (Google Drive y Microsoft OneDrive)',
      paragraphs: [
        'Un administrador de tu servidor PSS_MAN puede conectar una cuenta de Google Drive o de Microsoft OneDrive para hacer copias automáticas. No se sube nada si esto no está configurado.',
      ],
      bullets: [
        'Las copias se suben directamente desde tu servidor a tu propia cuenta en la nube. Nunca pasan por el desarrollador ni por ningún otro servicio.',
        'El acceso a Google Drive usa el permiso "drive.file", así que PSS_MAN solo puede ver y gestionar los archivos que él mismo creó. No puede ver ninguno de tus otros archivos.',
        'El acceso a OneDrive usa el permiso "Files.ReadWrite.AppFolder", que limita PSS_MAN a su propia carpeta de aplicación.',
        'Los archivos de copia contienen los archivos de tu bóveda, que ya están cifrados. Opcionalmente pueden sellarse por segunda vez con una frase de contraseña de copia.',
        'Los tokens de acceso que permiten subir las copias se guardan cifrados en tu servidor.',
        'Puedes desconectar la cuenta en cualquier momento desde PSS_MAN y revocar el acceso desde la configuración de tu cuenta de Google o de Microsoft.',
      ],
    },
    {
      heading: 'Datos de las API de Google',
      paragraphs: [
        'El uso y la transferencia que hace PSS_MAN de la información recibida de las API de Google cumplen la Política de datos de usuario de los servicios de API de Google, incluidos los requisitos de uso limitado. Los datos obtenidos mediante las API de Google se usan únicamente para guardar y gestionar los archivos de copia de PSS_MAN en tu propio Google Drive. No se usan para publicidad, no se venden, no se transfieren a nadie más y no los leen personas.',
      ],
    },
    {
      heading: 'Otros servicios de terceros',
      bullets: [
        'La interfaz web carga su tipografía (Inter) desde Google Fonts, así que tu navegador se conecta a los servidores de fuentes de Google cuando abres PSS_MAN. A esa conexión se aplica la política de privacidad de Google.',
        'Tu servidor solo contacta con Google y Microsoft si conectas la copia de seguridad en la nube.',
      ],
    },
    {
      heading: 'Conservación y eliminación de datos',
      paragraphs: [
        'Como los datos están en tu servidor y en tus propias cuentas, tú decides cuánto tiempo se conservan. Puedes eliminar entradas y usuarios dentro de PSS_MAN, borrar el directorio de datos del servidor, borrar los archivos de copia de tu Google Drive u OneDrive y quitar la extensión de Chrome.',
      ],
    },
    {
      heading: 'Menores',
      paragraphs: [
        'PSS_MAN no está dirigido a menores de 13 años, y el desarrollador no recopila a sabiendas información de menores.',
      ],
    },
    {
      heading: 'Cambios en esta política',
      paragraphs: [
        'Si PSS_MAN empieza a tratar los datos de otra forma, esta página se actualizará y cambiará la fecha de entrada en vigor indicada arriba.',
      ],
    },
    {
      heading: 'Contacto',
      paragraphs: ['Puedes enviar preguntas sobre esta política a:'],
      email: CONTACT_EMAIL,
    },
  ],
};

const policies = { en, es };

// Spanish for Spanish readers; English (the authoritative text) for everyone else.
export function getPssManPrivacy(lang) {
  return lang === 'es' ? es : en;
}

export default policies;
